import {PERIOD_BUDGET_TYPE, PeriodBudgetType} from '@/constants';
import Moment from 'moment';
import React, {useMemo} from 'react';
import {FormattedMessage, MessageDescriptor, useIntl} from 'react-intl';
import styled from 'styled-components';
import Util from '../../../shared/util/util';
import {sortByStartDate} from './RetainerPeriodUtil';
import {FlexColumn, FlexRow} from '@forecast-it/design-system';

const TotalsContainer = styled(FlexColumn)`
	flex: 0 0 300px;
	margin-left: 8px;
	overflow: hidden;
`;

const TotalsHeader = styled.div`
	font-size: 16px;
	font-weight: 600;
	color: #535353;
`;

const TotalsContents = styled.div`
	display: flex;
	flex-direction: column;
	margin-top: 14px;
	border: 1px solid #e6e6e6;
	> div:not(:last-child) {
		border-bottom: 1px solid #e6e6e6;
	}
`;

const TotalsRow = styled(FlexRow)`
	flex: 0 0 auto;
	background: #f3f3f3;
	gap: 20px;
	padding: 15px 20px 15px 20px;
`;

const ConflictedRow = styled(TotalsRow)`
	background-color: #ec4b4b;
	> div {
		color: #fff;
	}
`;

const TotalsRowTitle = styled.div`
	flex: 1 1 auto;
	font-size: 13px;
	white-space: normal;
`;

const TotalsRowData = styled.div`
	flex: 0 0 auto;
	font-size: 18px;
	text-align: right;
`;

const SmallFontData = styled(TotalsRowData)`
	font-size: 14px;
`;

type Period = {
	node: {
		startYear: number;
		startMonth: number;
		startDay: number;
		endYear: number;
		endMonth: number;
		endDay: number;
		financialNumbers: {
			billableActualMinutes: number;
			billableActualTimeAndExpenses: number;
			actualCost: number;
			actualProfit: number;
			retainerPeriodTargetPrice: number;
			retainerPeriodRolloverPrice: number;
			retainerPeriodTargetMinutes: number;
			retainerPeriodRolloverMinutes: number;
		};
	};
};

type Project = {
	defaultPeriodBudgetType: PeriodBudgetType;
	retainerPeriods: {
		edges: Period[];
	};
	defaultPeriodHoursAmount: number;
	defaultPeriodPriceAmount: number;
	projectStartYear: number;
	projectStartMonth: number;
	projectStartDay: number;
	projectEndYear: number;
	projectEndMonth: number;
	projectEndDay: number;
};

type RetainerTotalsProps = {
	hasTarget: boolean;
	project: Project;
	withSuffix: (val: number, asText?: boolean) => string | JSX.Element;
	projectTotal: number;
	totalConflicted: number;
};

const getRetainerTypeTranslation = (
	retainerType: PeriodBudgetType,
	formatMessage: (descriptor: MessageDescriptor) => string
) => {
	switch (retainerType) {
		case PERIOD_BUDGET_TYPE.FIXED_HOURS:
			return formatMessage({id: 'common.fixed_hours'});
		case PERIOD_BUDGET_TYPE.FIXED_PRICE:
			return formatMessage({id: 'common.fixed_price'});
		case PERIOD_BUDGET_TYPE.TIME_AND_MATERIALS:
			return formatMessage({id: 'common.time_material'});
		default:
			return formatMessage({id: 'new_project_modal.retainer'});
	}
};

// Memoized sub-component for individual rows
const TotalsRowComponent = React.memo<{title: string; data: string | JSX.Element}>(({title, data}) => (
	<TotalsRow>
		<TotalsRowTitle>{title}</TotalsRowTitle>
		<TotalsRowData>{data}</TotalsRowData>
	</TotalsRow>
));

const RetainerTotalsComponent = ({
	hasTarget,
	project,
	withSuffix,
	projectTotal,
	totalConflicted,
}: RetainerTotalsProps): JSX.Element | null => {
	const {formatMessage} = useIntl();
	const formatData = useMemo(() => (val: number) => withSuffix(val, true), [withSuffix]);

	const {isTimeMaterial, isFixedPrice, inHours} = useMemo(
		() => ({
			isTimeMaterial: project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.TIME_AND_MATERIALS,
			isFixedPrice: project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_PRICE,
			inHours:
				project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.FIXED_HOURS ||
				project.defaultPeriodBudgetType === PERIOD_BUDGET_TYPE.TIME_AND_MATERIALS,
		}),
		[project.defaultPeriodBudgetType]
	);

	const sortedPeriods = useMemo(
		() => [...project.retainerPeriods.edges].sort(sortByStartDate),
		[project.retainerPeriods.edges]
	);

	const formattedDates = useMemo(() => {
		const firstPeriod = sortedPeriods[0];
		const lastPeriod = sortedPeriods[sortedPeriods.length - 1];

		const projectStart = Util.CreateMomentDate(
			project.projectStartYear,
			project.projectStartMonth,
			project.projectStartDay
		);
		const projectEnd = Util.CreateMomentDate(project.projectEndYear, project.projectEndMonth, project.projectEndDay);

		const start = firstPeriod
			? Util.CreateMomentDate(firstPeriod.node.startYear, firstPeriod.node.startMonth, firstPeriod.node.startDay)
			: projectStart;
		const end = lastPeriod
			? Util.CreateMomentDate(lastPeriod.node.endYear, lastPeriod.node.endMonth, lastPeriod.node.endDay)
			: projectEnd;

		const dates = start && end ? `${start.format('DD MMM YYYY')} - ${end.format('DD MMM YYYY')}` : '';

		return dates;
	}, [sortedPeriods, project]);

	// Memoize past periods and calculate all averages in one pass
	const averages = useMemo(() => {
		const today = Moment();
		const pastPeriods = project.retainerPeriods.edges
			.map(period => period.node)
			.filter(node => {
				const periodEndDate = Util.CreateMomentDate(node.endYear, node.endMonth, node.endDay);
				return periodEndDate.isBefore(today, 'day');
			});

		if (!pastPeriods.length) {
			return {
				billableValueOfService: 0,
				price: 0,
				cost: 0,
				profit: 0,
				targetHours: 0,
				actualHours: 0,
			};
		}

		// Calculate all sums in a single reduce pass
		const sums = pastPeriods.reduce(
			(acc, node) => ({
				billableValue: acc.billableValue + node.financialNumbers.billableActualTimeAndExpenses,
				price:
					acc.price +
					node.financialNumbers.retainerPeriodTargetPrice +
					node.financialNumbers.retainerPeriodRolloverPrice,
				cost: acc.cost + node.financialNumbers.actualCost,
				profit: acc.profit + node.financialNumbers.actualProfit,
				targetMinutes:
					acc.targetMinutes +
					node.financialNumbers.retainerPeriodTargetMinutes +
					node.financialNumbers.retainerPeriodRolloverMinutes,
				actualMinutes: acc.actualMinutes + node.financialNumbers.billableActualMinutes,
			}),
			{
				billableValue: 0,
				price: 0,
				cost: 0,
				profit: 0,
				targetMinutes: 0,
				actualMinutes: 0,
			}
		);

		const count = pastPeriods.length;
		return {
			billableValueOfService: sums.billableValue / count,
			price: sums.price / count,
			cost: sums.cost / count,
			profit: sums.profit / count,
			targetHours: sums.targetMinutes / 60 / count,
			actualHours: sums.actualMinutes / 60 / count,
		};
	}, [project.retainerPeriods.edges]);

	// Memoize retainer type translation
	const retainerTypeLabel = useMemo(
		() => getRetainerTypeTranslation(project.defaultPeriodBudgetType, formatMessage),
		[project.defaultPeriodBudgetType, formatMessage]
	);

	return (
		<TotalsContainer>
			<TotalsHeader>
				<FormattedMessage id={'retainer.retainer_totals'} />
			</TotalsHeader>
			<TotalsContents data-userpilot="retainer-dashboard">
				<TotalsRow>
					<TotalsRowTitle>
						<FormattedMessage id={'retainer.type'} />
					</TotalsRowTitle>
					<SmallFontData>{retainerTypeLabel}</SmallFontData>
				</TotalsRow>
				<TotalsRowComponent
					title={formatMessage({id: 'retainer.periods'})}
					data={String(project.retainerPeriods.edges.length)}
				/>

				{isTimeMaterial && (
					<TotalsRow>
						<TotalsRowTitle>
							<FormattedMessage id={'retainer.period_dates'} />
						</TotalsRowTitle>
						<SmallFontData>{formattedDates}</SmallFontData>
					</TotalsRow>
				)}

				{hasTarget && (
					<>
						<TotalsRowComponent
							title={formatMessage({id: inHours ? 'retainer.total_target_hours' : 'retainer.total_period_price'})}
							data={formatData(projectTotal)}
						/>
						<TotalsRowComponent
							title={formatMessage({id: 'retainer.default_period_target'})}
							data={formatData(inHours ? project.defaultPeriodHoursAmount : project.defaultPeriodPriceAmount)}
						/>
					</>
				)}

				{isFixedPrice && (
					<>
						<TotalsRowComponent
							title={formatMessage({id: 'retainer.past_periods_average_billable_value_of_service'})}
							data={formatData(averages.billableValueOfService)}
						/>
						<TotalsRowComponent
							title={formatMessage({id: 'retainer.past_periods_average_price'})}
							data={formatData(averages.price)}
						/>
						<TotalsRowComponent
							title={formatMessage({id: 'retainer.past_periods_average_cost'})}
							data={formatData(averages.cost)}
						/>
						<TotalsRowComponent
							title={formatMessage({id: 'retainer.past_periods_average_profit'})}
							data={formatData(averages.profit)}
						/>
					</>
				)}

				{!isFixedPrice && (
					<>
						<TotalsRowComponent
							title={formatMessage({id: 'retainer.past_periods_average_target_hours'})}
							data={formatData(averages.targetHours)}
						/>
						<TotalsRowComponent
							title={formatMessage({id: 'retainer.past_periods_average_actual_billable_hours'})}
							data={formatData(averages.actualHours)}
						/>
					</>
				)}

				{!isTimeMaterial && totalConflicted > 0 && (
					<ConflictedRow>
						<TotalsRowTitle>
							<FormattedMessage id={inHours ? 'retainer.conflicted_time' : 'retainer.conflicted_price'} />
						</TotalsRowTitle>
						<TotalsRowData data-cy={'dashboard-time-entry-conflict-text'}>
							{formatData(totalConflicted)}
						</TotalsRowData>
					</ConflictedRow>
				)}
			</TotalsContents>
		</TotalsContainer>
	);
};

export const RetainerTotals = React.memo(RetainerTotalsComponent);
